/**
 * ч.1218: «не проведено за задумом» — перенесені з 1С документи, які навмисно лишаються
 * непроведеними (рахунок закрито при імпорті, відпустку перенесено «як є», проводки приїхали
 * окремою довідкою). Для них у списках і в редакторі показуємо нейтральну сіру відмітку з
 * поясненням, а не червону «не проведено».
 *
 * Правило живе на сервері (GET /documents/unposted-notes); тут — лише запит для рядків
 * сторінки. Питаємо тільки про перенесені й непроведені рядки, тож для звичайних списків
 * зайвих запитів немає.
 */
import { api } from '@/api/client';

export interface UnpostedNote {
  code: 'ORDER' | 'LEAVE' | 'CLONED' | 'DRAFT_1C' | 'DELETED_1C';
  text: string;
  /** Підпис відмітки у редакторі (ч.1247); старий сервер його не шле. */
  label?: string;
}

/** ч.1247: пояснення чернетки 1С («У 1С цей документ збережено, але не проведено…» /
 *  «…позначено на видалення…») — такий документ провести МОЖНА, на відміну від решти класів. */
export const isDraft1cNote = (note: string | null | undefined): boolean =>
  !!note && note.startsWith('У 1С цей документ');

/** Тип документа (таблиця) рядка: сталий для списку або за рядком (реєстри з кількох видів). */
export type DocTypeOf<T> = string | ((row: T) => string | undefined);

interface NoteRow { id?: string; posted?: boolean; external_source?: string | null; _unposted_note?: string }

export const fetchUnpostedNotes = (documentType: string, ids: string[]) =>
  api.get<Record<string, UnpostedNote>>('/documents/unposted-notes', {
    params: { document_type: documentType, ids: ids.join(',') },
  }).then((r) => r.data);

/** Рядки, про які варто питати: перенесені (є джерело) і непроведені. */
export function unpostedCandidates<T>(rows: T[], docType: DocTypeOf<T>): Map<string, string[]> {
  const byType = new Map<string, string[]>();
  for (const row of rows) {
    const r = row as NoteRow;
    if (!r.id || r.posted !== false || !r.external_source) continue;
    const type = typeof docType === 'function' ? docType(row) : docType;
    if (!type) continue;
    byType.set(type, [...(byType.get(type) ?? []), r.id]);
  }
  return byType;
}

/** Дописує рядкам сторінки `_unposted_note` (текст пояснення). Збій запиту не критичний:
 *  рядки просто лишаються зі звичайною відміткою. */
export async function decorateUnpostedNotes<T>(rows: T[], docType: DocTypeOf<T>): Promise<void> {
  const byType = unpostedCandidates(rows, docType);
  if (byType.size === 0) return;
  const notes: Record<string, UnpostedNote> = {};
  await Promise.all([...byType].map(async ([type, ids]) => {
    try { Object.assign(notes, await fetchUnpostedNotes(type, ids)); } catch { /* не критично */ }
  }));
  for (const row of rows) {
    const r = row as NoteRow;
    if (r.id && notes[r.id]) r._unposted_note = notes[r.id]!.text;
  }
}

/** Пояснення з рядка: списки кладуть його в `_unposted_note`, зведені журнали — в `unposted_note`. */
export function unpostedNoteOf(row: unknown): string | undefined {
  const r = row as { _unposted_note?: string | null; unposted_note?: string | null } | null | undefined;
  return r?._unposted_note || r?.unposted_note || undefined;
}
